import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { Eye, UserRoundX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_STOP_IMPERSONATE } from '~/gql/auth/auth'
import { clearImpersonationOrigin, isPlatformImpersonation } from '~/helpers/impersonation/origin'
import { useAuth } from '~/helpers/store/auth'
import { userDisplayName } from '~/helpers/users/display-name'
import { SYSTEM_ORG_SLUG } from '~/routes/orgRouting'
import { getDefaultRoute } from '~/routes/protected-loader'

/**
 * Bandeau affiché en haut de l'application pendant une session d'impersonation
 * (payload.impersonatorId présent). Permet à l'admin de revenir à son propre
 * compte via la mutation stopImpersonate, qui ré-émet ses cookies de session.
 */
function ImpersonationBanner() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { payload, setPayload } = useAuth()

  const [stopImpersonate, { loading }] = useMutation(GQL_STOP_IMPERSONATE)

  if (!payload?.impersonatorId) return null

  /*
   * Session d'usurpateur irrécupérable : on repart de zéro. Un opérateur
   * plateforme n'a pas d'org, son logout est celui de `/o/_system` — il faut
   * changer de basename (chargement complet), `navigate('/logout')` resterait
   * sous celui de l'org quittée.
   */
  const leaveToLogout = () => {
    toast.error(t('impersonate.exit.failed'))
    if (isPlatformImpersonation()) {
      clearImpersonationOrigin()
      window.location.href = `/o/${SYSTEM_ORG_SLUG}/logout`
      return
    }
    navigate('/logout')
  }

  const onStop = async () => {
    try {
      const { data } = await stopImpersonate()
      if (data?.stopImpersonate.__typename === 'UserInfo') {
        setPayload({
          firstName: data.stopImpersonate.firstName,
          lastName: data.stopImpersonate.lastName,
          email: data.stopImpersonate.email,
          username: data.stopImpersonate.username,
          role: data.stopImpersonate.roles,
          id: data.stopImpersonate.id,
        })
        const role = data.stopImpersonate.roles as RoleEnum
        const route = getDefaultRoute(role)
        clearImpersonationOrigin()

        /*
         * Un opérateur plateforme quitte l'espace du client : il faut CHANGER
         * de basename, pas seulement de route. `navigate` reste sous le
         * `/o/<client>` monté au chargement, et le super-admin serait revenu
         * sur son back-office… à l'adresse de l'organisation qu'il vient de
         * quitter. Seul un vrai chargement de page remonte le routeur ailleurs.
         */
        if (role === ('SuperAdmin' as RoleEnum)) {
          window.location.href = `/o/${SYSTEM_ORG_SLUG}${route}`
          return
        }

        navigate(route)
      } else {
        // Compte rétrogradé/désactivé.
        leaveToLogout()
      }
    } catch {
      leaveToLogout()
    }
  }

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <Eye className="size-4 shrink-0" />
      <span className="font-medium">
        {t('impersonate.banner.message', {
          name: userDisplayName(payload),
        })}
      </span>
      {/*
        Rappel permanent : seul le tracking SCORM est neutralisé pendant
        l'impersonation, le reste des mutations part pour de vrai au nom
        de l'utilisateur consulté.
      */}
      <span className="opacity-80">
        {t('impersonate.banner.warning')}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-400 bg-transparent hover:bg-amber-200 dark:border-amber-600 dark:hover:bg-amber-900"
        disabled={loading}
        onClick={onStop}
      >
        <UserRoundX className="size-4 mr-1" />
        {t('impersonate.banner.exit')}
      </Button>
    </div>
  )
}

export default ImpersonationBanner
